/**
 * Design tokens for the shared-axis Cowbell Factors panel.
 *
 * The panel is a chart, and its readability depends on a deliberate ramp of
 * blues and neutrals rather than the app's general-purpose palette: the range
 * band has to recede, the industry tick has to read as a hard reference line,
 * and the company dot has to be the first thing the eye lands on. The light
 * values are the design's; dark mode re-derives the same roles from the theme
 * so the ramp survives the inversion.
 */
export interface FactorRangePalette {
  /** Score text, factor names. */
  readonly ink: string;
  /** Row descriptions, the "vs" comparison. */
  readonly inkMuted: string;
  /** Axis numbers, range bounds. */
  readonly inkFaint: string;
  /** Gridlines, row dividers. */
  readonly line: string;
  /** The legend strip behind the key. */
  readonly surfaceMuted: string;
  /** The paper the dot's ring is punched out of. */
  readonly surface: string;
  /** The company score dot. */
  readonly brand: string;
  /** The industry range band. */
  readonly band: string;
  /** The band's end caps. */
  readonly bandCap: string;
  /** The industry-average tick. */
  readonly reference: string;
  /** Upward trend. */
  readonly positive: string;
  /** Below-average score, dot and downward trend. */
  readonly critical: string;
}

const LIGHT: FactorRangePalette = {
  ink: '#0d1521',
  inkMuted: '#5c6b85',
  inkFaint: '#8593aa',
  line: '#e2e8f0',
  surfaceMuted: '#f7f9fc',
  surface: '#ffffff',
  brand: '#0f62d8',
  band: '#d9e8ff',
  bandCap: '#b6d2ff',
  reference: '#33415c',
  positive: '#1f8a56',
  critical: '#c0392b',
};

const DARK: FactorRangePalette = {
  ink: '#e8edf5',
  inkMuted: '#9aa7bd',
  inkFaint: '#77839a',
  line: '#333a47',
  surfaceMuted: 'rgba(255, 255, 255, 0.03)',
  surface: '#26262e',
  brand: '#5a9bff',
  band: 'rgba(90, 155, 255, 0.22)',
  bandCap: 'rgba(90, 155, 255, 0.45)',
  reference: '#c3ccdb',
  positive: '#4caf7d',
  critical: '#f07167',
};

export const getFactorRangePalette = (mode?: string): FactorRangePalette =>
  mode === 'dark' ? DARK : LIGHT;

export { rem } from '../../panels/panelLayout';
